import {useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import { jwtDecode } from "jwt-decode";
import {useHandler} from "./useHandler";
import EventBus from "./EventBus";

type PickValue = {
    USID: string,
    Name: string,
    Group: string,
    Order: number,
    Type: string,
    Value: string
};

type Position = {
    USID: string,
    Name: string,
    OrganizationUSID: string,
    ParentUSID?: string | null
};

// Positions ABOVE the ones this account user holds. Display context for the persona picker only --
// they are what connects held positions into a tree, since a user's own positions frequently do not
// touch each other. Kept out of Positions on purpose: that list is both the authorization list and,
// in this tier, what derives work group membership.
type PositionAncestor = {
    USID: string,
    Name: string,
    ParentUSID?: string | null
};

type PrivilegeConstraint = {
    Name: string,
    Type: string,
    ParentUSID: string,
    AccountUserUSID: string
};

type UserGroup = {
    USID: string,
    Name: string
};

type WorkGroup = {
    USID: string,
    Name: string
};

type Role = {
    Name: string,
    USID: string
};

export type Visibility = {
    Mode: string,
    Value: string
}

export type Tenant = {
    USID: string,
    Name: string
}

export interface Account {
    Positions: [Position],
    PositionAncestors?: PositionAncestor[],
    UserGroups: [UserGroup],
    WorkGroups: [WorkGroup]
}

export class SessionContext {
    sessionUSID: string | null;
    sid: string | null;
    identityToken: string | null;
    accessToken: string | null;
    accessTokenClaims: string | null;
    identityTokenClaims: string | null;
    sessionId: string;
    refreshToken: string | null;
    showLoginDialog: boolean;
    isLoggedIn: boolean;
    activeVisibility: Visibility | null;
    account: Account | null;
    pickValues: PickValue[];
    roles: Role[];
    tenants: Tenant[];
    privileges: string[];
    privilegeConstraints: PrivilegeConstraint[];
    generalPurposeVariable: string | null;
    S3AccessKeyId: string | null;
    S3SecretAccessKey: string | null;
    S3SessionToken: string | null;
    url: string | null;

    name: string | null;
    email: string | null;
    accountName: string | null;
    accountUserUSID: string | null;
    realm: string | null;
    userUSID: string | null;

    constructor() {
        this.sid = null;
        this.sessionUSID = null;
        this.identityToken = null;
        this.accessToken = null;
        this.accessTokenClaims = null;
        this.accountName = null;
        this.accountUserUSID = null;
        this.identityTokenClaims = null;
        this.refreshToken = null;
        this.name = null;
        this.email = null;
        this.realm = null;
        this.userUSID = null;
        this.showLoginDialog = true;
        this.isLoggedIn = false;
        this.activeVisibility = null;
        this.account = null;
        this.pickValues = []
        this.roles = [];
        this.privileges = [];
        this.privilegeConstraints = [];
        this.tenants = [];
        this.generalPurposeVariable = null;
        this.S3AccessKeyId = null;
        this.S3SecretAccessKey = null;
        this.S3SessionToken = null;
        this.url = null;

        this.sessionId = SessionContext.loadSessionId();
    }

    static loadSessionId() {
        let sessionId: string | null = sessionStorage.getItem("sessionId");
        if (sessionId === null) {
            sessionId = uuidv4();
            sessionStorage.setItem("sessionId", sessionId);
        }
        return sessionId;
    }

    convert(type: any, value: any) {
        if (value === "null") value = null;
        if (value === null) return null;
        if (value === undefined) return undefined;
        if (typeof value === "string") {
            if (type === Boolean) return value === "true";
            if (type === Object) return JSON.parse(value);
        }
        return value;
    }

    getAccessToken(): string | null {
        this.accessToken = sessionStorage.getItem("AccessToken")

        return this.accessToken;
    }

    getAccessTokenClaims(): any | null {
        this.accessTokenClaims = JSON.parse(sessionStorage.getItem("AccessTokenClaims")!)

        return this.accessTokenClaims;
    }

    getActiveVisibility(): Visibility | null {
        this.activeVisibility = JSON.parse(sessionStorage.getItem("ActiveVisibility")!)

        if (this.activeVisibility == null) {
            return {
                Mode: "",
                Value: ""
            };
        }
        return this.activeVisibility;
    }

    getAccount(): Account | null {
        this.account = this.convert(Object, sessionStorage.getItem("Account"))

        return this.account;
    }

    getAccountName(): string | null {
        this.accountName = sessionStorage.getItem("AccountName")

        return this.accountName;
    }

    getAccountUserUSID(): string | null {
        this.accountUserUSID = sessionStorage.getItem("AccountUserUSID")

        return this.accountUserUSID;
    }

    getGeneralPurposeVariable(): string | null {
        this.generalPurposeVariable = sessionStorage.getItem("GeneralPurposeVariable")

        return this.generalPurposeVariable;
    }

    getIdentityToken(): string | null {
        this.identityToken = sessionStorage.getItem("IdentityToken")

        return this.identityToken;
    }

    getIdentityTokenClaims(): string | null {
        this.identityTokenClaims = sessionStorage.getItem("IdentityTokenClaims")

        return this.identityTokenClaims;
    }

    getName(): string {
        this.name = sessionStorage.getItem("Name")!

        return this.name;
    }

    getPermissionConstraints(): PrivilegeConstraint[] {
        this.privilegeConstraints = JSON.parse(sessionStorage.getItem("PrivilegeConstraints")!)

        return this.privilegeConstraints;
    }

    getPickValues(): PickValue[] {
        this.pickValues = JSON.parse(sessionStorage.getItem("PickValues")!)

        return this.pickValues;
    }

    /**
     * The products this person's roles give them something to do in.
     *
     * DECIDED BY THE SERVER, at login, from the privileges their roles grant -- see
     * cres/utils/products.py. Not recomputed here: a second copy of "which privileges belong to
     * which product" in the browser would be a second thing to keep in step with the catalog, and
     * the copy that drifted would be the one deciding what people can see.
     *
     * AN EMPTY ANSWER MEANS "NOT ASKED", NOT "NOTHING". A session signed in before this existed has
     * no list, and hiding every product from somebody who is legitimately signed in would be a
     * worse failure than showing one door too many for one more login.
     */
    getProducts(): string[] | null {
        const stored = sessionStorage.getItem("Products");
        if (!stored) return null;
        try { return JSON.parse(stored); } catch { return null; }
    }

    getPrivileges(): string[] {
        this.privileges = JSON.parse(sessionStorage.getItem("Privileges")!)

        if (this.privileges === null) {
            return []
        }
        return this.privileges;
    }

    getRealm(): string | null {
        this.realm = sessionStorage.getItem("Realm")!

        return this.realm;
    }

    getRefreshToken(): string | null {
        this.refreshToken = sessionStorage.getItem("RefreshToken")

        return this.refreshToken;
    }

    getRoles(): Role[] {
        this.roles = JSON.parse(sessionStorage.getItem("Roles")!)

        return this.roles;
    }

    getSID(): string | null {
        this.sid = sessionStorage.getItem("SID")

        return this.sid;
    }

    getS3AccessKeyId(): string | null {
        this.S3AccessKeyId = sessionStorage.getItem("S3AccessKeyId")

        return this.S3AccessKeyId;
    }

    getS3SecretAccessKey(): string | null {
        this.S3SecretAccessKey = sessionStorage.getItem("S3SecretAccessKey")

        return this.S3SecretAccessKey;
    }

    getS3SessionToken(): string | null {
        this.S3SessionToken = sessionStorage.getItem("S3SessionToken")

        return this.S3SessionToken;
    }

    getShowLoginDialog(): boolean {
        this.showLoginDialog = sessionStorage.getItem("ShowLoginDialog") === 'true'

        return this.showLoginDialog;
    }

    getTenants(): Tenant[] {
        this.tenants = JSON.parse(sessionStorage.getItem("Tenants")!)

        return this.tenants;
    }

    getURL(): string | null {
        this.url = sessionStorage.getItem("URL")

        return this.url;
    }

    getUserUSID(): string | null {
        this.userUSID = sessionStorage.getItem("UserUSID")

        return this.userUSID;
    }

    jwtDecode(token: string | null): any {
        if (token === null) {
            return null;
        }
        try {
            return jwtDecode(token);
        } catch (e) {
            return null;
        }
    }

    setAccountName(accountName: string) {
        this.accountName = accountName;
        sessionStorage.setItem("AccountName", accountName)
        EventBus.session.emit("update", {"AccountName": this.accountName});
    }

    setAccessToken(accessToken: string) {
        this.accessToken = accessToken;
        sessionStorage.setItem("AccessToken", accessToken)

        const claims: any = this.jwtDecode(accessToken);

        this.setAccessTokenClaims(claims);
        EventBus.session.emit("update", {"AccessToken": this.accessToken});
    }

    setAccessTokenClaims(claims: string) {
        this.accessTokenClaims = claims
        sessionStorage.setItem("AccessTokenClaims", JSON.stringify(this.accessTokenClaims))
        EventBus.session.emit("update", {"AccessTokenClaims": JSON.stringify(this.accessTokenClaims)});
    }

    setActiveVisibility(visibilityMode: string, visibilityValue: string) {
        this.activeVisibility = {
            Mode: visibilityMode,
            Value: visibilityValue
        }
        sessionStorage.setItem("ActiveVisibility", JSON.stringify(this.activeVisibility))
        EventBus.session.emit("update", {"ActiveVisibility": this.activeVisibility});
    }

    setAccount(account: Account) {
        this.account = account
        sessionStorage.setItem("Account", JSON.stringify(account))
        EventBus.session.emit("update", {"Account": this.account});
    }

    setEmail(email: string) {
        this.email = email
        sessionStorage.setItem("EMail", this.email)
        EventBus.session.emit("update", {"EMail": this.email});
    }

    setAccountUserUSID(accountUserUSID: string) {
        this.accountUserUSID = accountUserUSID
        sessionStorage.setItem("AccountUserUSID", this.accountUserUSID)
        EventBus.session.emit("update", {"AccountUserUSID": this.accountUserUSID});
    }

    setGeneralPurposeVariable(generalPurposeVariable: string) {
        this.generalPurposeVariable = generalPurposeVariable
        sessionStorage.setItem("GeneralPurposeVariable", this.generalPurposeVariable)
        EventBus.session.emit("update", {"GeneralPurposeVariable": this.generalPurposeVariable});
    }

    setIdentityTokenClaims(claims: string) {
        this.identityTokenClaims = claims
        sessionStorage.setItem("IdentityTokenClaims", this.identityTokenClaims)
        EventBus.session.emit("update", {"IdentityTokenClaims": this.identityTokenClaims});
    }

    setIdentityToken(identityToken: string) {
        this.identityToken = identityToken;
        sessionStorage.setItem("IdentityToken", identityToken)

        const claims: any = this.jwtDecode(identityToken);

        this.setIdentityTokenClaims(claims);
        this.setName(claims.name)
        this.setAccountName(claims.accountName);
        this.setEmail(claims.email);
        this.setAccountUserUSID(claims.accountUserUSID);
        this.setRealm(claims.realm);
        this.setUserUSID(claims.userUSID);
    }

    setName(name: string) {
        this.name = name
        sessionStorage.setItem("Name", this.name)
        EventBus.session.emit("update", {"Name": this.name});
    }

    setPickValues(pickValues: PickValue[]) {
        this.pickValues = pickValues
        sessionStorage.setItem("PickValues", JSON.stringify(pickValues))
        EventBus.session.emit("update", {"PickValues": this.pickValues});
    }

    setProducts(input: string[]) {
        sessionStorage.setItem("Products", JSON.stringify(input || []));
        EventBus.session.emit("update", {"Products": input || []});
    }

    setPrivileges(input: string[]) {
        this.privileges = input;
        sessionStorage.setItem("Privileges", JSON.stringify(this.privileges))
        EventBus.session.emit("update", {"Privileges": this.privileges});
    }

    setPrivilegeConstraints(input: PrivilegeConstraint[]) {
        this.privilegeConstraints = input;
        sessionStorage.setItem("PrivilegeConstraints", JSON.stringify(this.privilegeConstraints))
        EventBus.session.emit("update", {"PrivilegeConstraints": this.privilegeConstraints});
    }

    setRealm(realm: string) {
        this.realm = realm
        sessionStorage.setItem("Realm", this.realm)
        EventBus.session.emit("update", {"Realm": this.realm});
    }

    setRefreshToken(refreshToken: string) {
        this.refreshToken = refreshToken
        sessionStorage.setItem("RefreshToken", this.refreshToken)
        EventBus.session.emit("update", {"RefreshToken": this.refreshToken});
    }

    setRoles(roles: Role[]) {
        this.roles = roles
        sessionStorage.setItem("Roles", JSON.stringify(roles))

        EventBus.session.emit("update", {"Roles": this.roles});
    }

    setSID(sid: string) {
        this.sid = sid
        sessionStorage.setItem("SID", sid)
        EventBus.session.emit("update", {"SID": this.sid});
    }

    setS3AccessKeyId(S3AccessKeyId: string) {
        this.S3AccessKeyId = S3AccessKeyId
        sessionStorage.setItem("S3AccessKeyId", S3AccessKeyId)
        EventBus.session.emit("update", {"S3AccessKeyId": this.S3AccessKeyId});
    }

    setS3SecretAccessKey(S3SecretAccessKey: string) {
        this.S3SecretAccessKey = S3SecretAccessKey
        sessionStorage.setItem("S3SecretAccessKey", S3SecretAccessKey)
        EventBus.session.emit("update", {"S3SecretAccessKey": this.S3SecretAccessKey});
    }

    setS3SessionToken(S3SessionToken: string) {
        this.S3SessionToken = S3SessionToken
        sessionStorage.setItem("S3SessionToken", S3SessionToken)
        EventBus.session.emit("update", {"S3SessionToken": this.S3SessionToken});
    }

    setSessionUSID(sessionUSID: string) {
        this.sessionUSID = sessionUSID
        sessionStorage.setItem("SessionUSID", sessionUSID)
        EventBus.session.emit("update", {"SessionUSID": this.sessionUSID});
    }

    setShowLoginDialog(loginDialog: boolean) {
        this.showLoginDialog = loginDialog
        sessionStorage.setItem("ShowLoginDialog", loginDialog.toString())
        EventBus.session.emit("update", {"ShowLoginDialog": this.showLoginDialog});
    }

    setTenants(tenants: Tenant[]) {
        this.tenants = tenants
        sessionStorage.setItem("Tenants", JSON.stringify(tenants))

        EventBus.session.emit("update", {"Tenants": this.tenants});
    }

    setUserUSID(userUSID: string) {
        this.userUSID = userUSID
        sessionStorage.setItem("UserUSID", this.userUSID)
        EventBus.session.emit("update", {"UserUSID": this.userUSID});
    }

    setURL(url: string) {
        this.url = url;
        sessionStorage.setItem("URL", this.url)
    }

    ttl() {
        const accessTokenClaims = this.getAccessTokenClaims();
        if (!accessTokenClaims) return 0;
        return Date.now() / 1000 - accessTokenClaims.exp;
    }

    valid() {
        return Boolean(this.getIdentityTokenClaims() && this.getAccessTokenClaims());
    }

}

/**
 * May this person use this product?
 *
 * TRUE WHEN THE ANSWER IS UNKNOWN. See getProducts: a session that predates the product list, or a
 * tier that has not been told about a product yet, must not have its launcher emptied.
 */
export function hasProduct(product: string): boolean {
    const products = new SessionContext().getProducts();
    return products === null ? true : products.includes(product);
}

export function hasPrivilege(requiredPrivilege: string, projectPlanUSID?: string) {

    let sessionContext = new SessionContext();

    let privileges = sessionContext.getPrivileges();
    for (let j = 0; j < privileges.length; j++) {
        if (privileges[j] === "Administrator") return true;

        if (requiredPrivilege === privileges[j]) {
            if (projectPlanUSID === undefined) return true;

            let privilegeConstraints = sessionContext.getPermissionConstraints();
            for (let k = 0; k < privilegeConstraints.length; k++) {
                if (privilegeConstraints[k]["ParentUSID"] === projectPlanUSID && privilegeConstraints[k]["AccountUserUSID"] === sessionContext.getAccountUserUSID() && privilegeConstraints[k]["Name"] === requiredPrivilege && privilegeConstraints[k]["Type"] === "DENY_EMPLOYEE") {
                    return false
                }
            }
            return true
        }
    }

    return false
}


export function useSessionContext() {

    const [sessionContext, setSessionContext] = useState(new SessionContext());

    const updateListener = useHandler(
        () => setSessionContext(new SessionContext()),
        [sessionContext],
    );

    useEffect(() => {
        EventBus.session.addListener("update", updateListener);
        return () => {
            EventBus.session.removeListener("update", updateListener);
        }
    }, [updateListener]);

    return sessionContext;
}

